import { Breakpoint, CSSObject, Theme } from '@mui/material';

export const CONTAINER_NAME = 'main';

type ContainerGridConfig = Partial<Record<Breakpoint, number>>;

/**
 * Creates container-query based responsive grid styles for MUI Grid.
 * This utility generates CSS that adjusts element width based on its container size
 * rather than viewport size.
 *
 * @param {ContainerGridConfig} config - Breakpoint configuration object containing MUI breakpoints
 *                                      (xs, sm, md, lg, xl) and column counts (1-12)
 * @returns {Function} A theme function compatible with MUI's sx prop that generates
 *                    responsive grid styles based on container queries
 *
 * @example
 * // Basic usage with sx prop
 * <Grid sx={containerGrid({ xs: 12, md: 6, lg: 4 })} />
 *
 * // Combined with other styles
 * <Grid sx={[
 *   { display: 'flex' },
 *   containerGrid({ xs: 12, md: 6 })
 * ]} />
 *
 */
export const containerGrid =
  (config: ContainerGridConfig) =>
  (theme: Theme): CSSObject =>
    Object.entries(config).reduce<CSSObject>((acc, [breakpoint, columns]) => {
      if (columns) {
        const breakpointValue = theme.breakpoints.values[breakpoint as Breakpoint];
        const containerQuery = theme.containerQueries(CONTAINER_NAME).up(breakpointValue);
        acc[containerQuery] = {
          flexBasis: 'auto',
          flexGrow: 0,
          width: `calc(100% * ${columns} / var(--Grid-parent-columns) - (var(--Grid-parent-columns) - ${columns}) * (var(--Grid-parent-columnSpacing) / var(--Grid-parent-columns)))`,
        };
      }
      return acc;
    }, {});
